import React from 'react'

import { Button, ModalFooter } from '@clientbase/clientbase-library'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { ContaAzulInfo } from 'models/AccountsPayable'

import ContaAzulStatusChip from 'components/ContaAzulStatusChip'
import { Table } from 'components/Table'

// Linha já normalizada para exibição (cada tela formata dueDate/amount antes de passar).
export interface ContaAzulRecurrenceRow {
  uuid?: string
  cycle?: string | number
  dueDate?: string
  amount?: string
  contaAzulInfo?: ContaAzulInfo | null
}

// true quando ao menos um registro tem info do Conta Azul — usado pelas telas para decidir
// se exibem o accordion.
export const hasContaAzulInfo = (records?: ContaAzulRecurrenceRow[]): boolean =>
  !!records?.some((record) => record.contaAzulInfo)

// Estados que aparecem como "Sincronizando" no chip (sync em andamento). Enquanto algum
// ciclo estiver assim, o "Sincronizar novamente" da recorrência fica desabilitado.
const SYNCING_STATUSES = ['syncing', 'enqueued']

interface ContaAzulResyncFooterProps {
  records: ContaAzulRecurrenceRow[]
  onResync: () => void
  resyncing: boolean
  // Sincronizar com a Conta Azul e uma acao do MODULO dono do registro, nao da tela de
  // integracoes: o backend exige billings:manage no detalhe de cobranca e
  // accounts_payable:manage no de despesa. Quem so tem :show chega ate aqui e nao pode
  // disparar o reenvio — cada tela informa o valor.
  canManage: boolean
}

// Footer do detalhe CA da recorrência: reenvia TODOS os ciclos de uma vez (a notificação
// in-app volta consolidada). Fica desabilitado enquanto algum ciclo está sincronizando ou
// enquanto o próprio reenvio está em andamento. Botão vazado (o verde é p/ confirmações).
export const ContaAzulResyncFooter: React.FC<ContaAzulResyncFooterProps> = ({
  records,
  onResync,
  resyncing,
  canManage
}) => {
  const hasSyncing = records.some((record) =>
    SYNCING_STATUSES.includes(record.contaAzulInfo?.status ?? '')
  )

  // O footer so existe para abrigar o botao de reenvio; sem permissao ele nao aparece.
  if (!canManage) return null

  return (
    <ModalFooter>
      <Stack direction="row" justifyContent="flex-end" width="100%">
        <Tooltip
          arrow
          placement="top"
          disableHoverListener={!hasSyncing}
          title="Aguarde os ciclos em sincronização terminarem."
        >
          <span>
            <Button
              variantButton="outlined"
              startIcon="faRepeat"
              loading={resyncing}
              disabled={resyncing || hasSyncing}
              onClick={onResync}
            >
              Sincronizar novamente
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </ModalFooter>
  )
}

interface ContaAzulRecurrenceTableProps {
  records: ContaAzulRecurrenceRow[]
  syncingUuid: string | null
  onSync: (uuid?: string) => void
  // Ver nota em ContaAzulResyncFooterProps: a permissao e a do modulo, e a tabela e
  // compartilhada entre cobrancas e contas a pagar, entao quem decide e a tela.
  canManage: boolean
}

// Table do accordion "Conta Azul" das telas de detalhe de recorrência (cobranças / contas a
// pagar): status de sincronização de cada registro, erro (quando houver) e ação "Sincronizar"
// para os não-sincronizados. Reutilizado por ModalRecurrenceDetail e ModalDetailsExpenses;
// cada tela envolve num accordion do seu próprio padrão.
const ContaAzulRecurrenceTable: React.FC<ContaAzulRecurrenceTableProps> = ({
  records,
  syncingUuid,
  onSync,
  canManage
}) => {
  // A coluna "Erro" só aparece quando há ao menos um ciclo em erro. Como o corpo da Table
  // renderiza pelas chaves da row (na ordem), a chave `error` e a coluna do header precisam
  // ser omitidas juntas para não desalinhar.
  const hasError = records.some(
    (record) => record.contaAzulInfo?.status === 'error'
  )

  const rows = records.map((record) => {
    const info = record.contaAzulInfo
    const isError = info?.status === 'error'
    // Só permite sincronizar manualmente quando está em erro ou nunca sincronizou.
    // Estados em andamento (pending/enqueued/syncing) e o sincronizado não têm ação —
    // evita reenfileirar algo que já está a caminho.
    const canSync = canManage && (!info || isError)

    const statusChip = <ContaAzulStatusChip status={info?.status} />

    return {
      uuid: record.uuid,
      cycle: record.cycle ?? '—',
      dueDate: record.dueDate ?? '—',
      amount: record.amount ?? '—',
      status: statusChip,
      ...(hasError && {
        error:
          isError && info?.lastError ? (
            <Tooltip arrow placement="top" title={info.lastError}>
              <Typography
                variant="smLight"
                sx={{
                  color: '#D32F2F',
                  maxWidth: 240,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {info.lastError}
              </Typography>
            </Tooltip>
          ) : (
            ''
          )
      }),
      actions: !canSync ? (
        ''
      ) : (
        <Button
          variantButton="outlined"
          startIcon="faRepeat"
          loading={syncingUuid === record.uuid}
          disabled={syncingUuid === record.uuid}
          onClick={() => onSync(record.uuid)}
        >
          Sincronizar
        </Button>
      )
    }
  })

  return (
    <Box maxHeight="300px" overflow="auto">
      <Table
        mobileCards
        handleChangePage={() => null}
        handleChangeRowsPerPage={() => null}
        header={[
          { id: 'cycle', label: 'Ciclo' },
          { id: 'dueDate', label: 'Vencimento' },
          { id: 'amount', label: 'Valor' },
          { id: 'status', label: 'Status' },
          ...(hasError ? [{ id: 'error', label: 'Erro' }] : []),
          { id: 'actions', label: '' }
        ]}
        hiddePagination={true}
        page={1}
        rows={rows}
        rowsPerPage={Infinity}
        totalCount={0}
        select={{ useSelect: false }}
        hideLastRowSeparator
      />
    </Box>
  )
}

export default ContaAzulRecurrenceTable
